/**
 * Block clipboard — localStorage-backed so copy/paste works across pages
 * and browser tabs of the same origin. Ids are regenerated on paste
 * (store.pasteAt), so stored ids never collide; the cross-host hazard is
 * foreign type slugs (Manual blocks pasted into Content), which callers
 * gate via clipboardTypes() against the registry.
 */

import { canPlace } from './nestingRules';
import type { BlockNode } from './types';
import { isSharedBlock } from './types';

export const CLIPBOARD_KEY = 'pb2.clipboard.v1';

interface ClipboardPayload {
    v: 1;
    blocks: BlockNode[];
}

/**
 * Stores deep clones of the given subtrees.
 *
 * @param blocks  Blocks to copy (with values and children).
 * @param storage Storage backend; defaults to window.localStorage.
 * @return void No return expected.
 */
export function writeClipboard(blocks: BlockNode[], storage: Storage | null = defaultStorage()): void {
    const payload: ClipboardPayload = { v: 1, blocks: JSON.parse(JSON.stringify(blocks)) as BlockNode[] };

    try {
        storage?.setItem(CLIPBOARD_KEY, JSON.stringify(payload));
    } catch {
        // private mode / quota — copy silently unavailable
    }
}

/**
 * Reads the stored subtrees, null when absent or invalid.
 *
 * @param storage Storage backend; defaults to window.localStorage.
 * @return Cloned blocks or null.
 */
export function readClipboard(storage: Storage | null = defaultStorage()): BlockNode[] | null {
    try {
        const raw = storage?.getItem(CLIPBOARD_KEY);

        if (!raw) {
            return null;
        }

        const payload = JSON.parse(raw) as Partial<ClipboardPayload>;

        if (payload.v !== 1 || !Array.isArray(payload.blocks) || payload.blocks.length === 0) {
            return null;
        }

        return payload.blocks;
    } catch {
        return null;
    }
}

/**
 * Distinct non-shared type slugs across the subtrees — the registry
 * validation set for cross-host paste gating.
 *
 * @param blocks Clipboard subtrees.
 * @return Type slugs.
 */
export function clipboardTypes(blocks: BlockNode[]): string[] {
    const slugs = new Set<string>();

    const visit = (list: BlockNode[]): void => {
        for (const block of list) {
            if (!isSharedBlock(block)) {
                slugs.add(String(block.type));
            }

            if (block.children?.length) {
                visit(block.children);
            }
        }
    };

    visit(blocks);

    return [...slugs];
}

/**
 * The clipboard content when it may be pasted under the given parent —
 * null when empty, when a subtree type is unknown to the host (cross-host
 * copy), or when any block violates the nesting rules there. Shared gate
 * of the context menu and the Cmd+V shortcut.
 *
 * @param parent    Target parent block (null = content root).
 * @param typeKnown Whether the host registry knows a type slug.
 * @param storage   Storage backend; defaults to window.localStorage.
 * @return Pasteable blocks or null.
 */
export function readPasteable(
    parent: BlockNode | null,
    typeKnown: (slug: string) => boolean,
    storage: Storage | null = defaultStorage(),
): BlockNode[] | null {
    const blocks = readClipboard(storage);

    if (blocks === null || !clipboardTypes(blocks).every(typeKnown)) {
        return null;
    }

    return blocks.every((block) => canPlace(block, parent)) ? blocks : null;
}

function defaultStorage(): Storage | null {
    return typeof localStorage === 'undefined' ? null : localStorage;
}
